import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Text,
  VStack,
  HStack,
  Icon,
  Flex,
  Spinner,
  Button,
  Badge,
  useColorModeValue,
  Tooltip,
  Grid,
  GridItem,
  Progress,
} from '@chakra-ui/react';
import { 
  FaSearch, 
  FaChevronRight,
  FaTrophy,
  FaRocket,
  FaFire,
  FaCrown,
  FaArrowUp,
  FaKeyboard,
  FaGlobe,
  FaBuilding,
  FaLightbulb,
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { getReviews } from '../services/reviewService';
import { logger } from '../utils/logger';
import {
  hasCompetitiveIntel,
  getSEOKeywords,
  getTopKeywordsByVolume,
  getKeywordOpportunities,
  getTotalOrganicKeywords,
  formatNumber,
} from '../utils/competitiveIntelHelpers';

/**
 * KeywordIntelligenceWidget - Displays keyword intelligence summary on the home screen
 * Redesigned for better data density and visual hierarchy
 */
const KeywordIntelligenceWidget = () => {
  const navigate = useNavigate();
  const [reviewsData, setReviewsData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Color mode values
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const subtitleColor = useColorModeValue('gray.500', 'gray.400');
  const textColor = useColorModeValue('gray.700', 'gray.300');
  const headerBg = useColorModeValue('gray.50', 'gray.900');
  const iconBg = useColorModeValue('gray.100', 'gray.800');

  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      try {
        const reviewsResult = await getReviews();
        
        if (mounted) {
          setReviewsData(reviewsResult || []);
          setIsLoading(false);
        }
      } catch (error) {
        logger.error('Error fetching data for Keyword Intelligence Widget:', error);
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      mounted = false;
    };
  }, []);

  // Calculate keyword intelligence metrics
  const keywordMetrics = useMemo(() => {
    const businesses = Array.isArray(reviewsData) ? reviewsData : reviewsData.reviews || [];
    
    if (!businesses || businesses.length === 0) {
      return { 
        totalBusinesses: 0,
        businessesWithKeywords: 0,
        totalKeywords: 0,
        totalSearchVolume: 0,
        topKeywords: [],
        keywordRankings: [],
        opportunities: [],
      };
    }

    // Filter businesses with keyword data
    const businessesWithKeywords = businesses.filter(business => {
      return hasCompetitiveIntel(business) && getSEOKeywords(business).length > 0;
    });

    if (!businessesWithKeywords.length) {
      return { 
        totalBusinesses: businesses.length,
        businessesWithKeywords: 0,
        totalKeywords: 0,
        totalSearchVolume: 0,
        topKeywords: [],
        keywordRankings: [],
        opportunities: [],
      };
    }

    // Calculate totals
    const totalKeywords = businessesWithKeywords.reduce((sum, business) => {
      return sum + getTotalOrganicKeywords(business);
    }, 0);

    // Get all top keywords across all businesses
    const allTopKeywords = [];
    businessesWithKeywords.forEach(business => {
      const topKws = getTopKeywordsByVolume(business, 5);
      topKws.forEach(kw => {
        allTopKeywords.push({
          ...kw,
          businessName: business.name
        });
      });
    });

    // Sort by search volume and get top keywords
    const topKeywords = allTopKeywords
      .sort((a, b) => (b.searchVolume || 0) - (a.searchVolume || 0))
      .slice(0, 8);

    // Get keyword rankings - keywords with rank data, sorted by search volume (highest first)
    const keywordRankings = allTopKeywords
      .filter(kw => kw.rank && kw.rank <= 100)
      .sort((a, b) => (b.searchVolume || 0) - (a.searchVolume || 0))
      .slice(0, 8);

    // Get all keyword opportunities, sorted by search volume (highest first)
    const allOpportunities = [];
    businessesWithKeywords.forEach(business => {
      const opps = getKeywordOpportunities(business, {
        minVolume: 100,
        maxDifficulty: 50,
        minRank: 11
      });
      opps.forEach(opp => {
        allOpportunities.push({
          ...opp,
          businessName: business.name
        });
      });
    });
    // Sort opportunities by search volume (highest first)
    allOpportunities.sort((a, b) => (b.searchVolume || 0) - (a.searchVolume || 0));

    // Calculate total search volume
    const totalSearchVolume = topKeywords.reduce((sum, kw) => sum + (kw.searchVolume || 0), 0);

    return {
      totalBusinesses: businesses.length,
      businessesWithKeywords: businessesWithKeywords.length,
      totalKeywords,
      totalSearchVolume,
      topKeywords,
      keywordRankings,
      opportunities: allOpportunities.slice(0, 6),
    };
  }, [reviewsData]);

  const handleViewDashboard = () => {
    navigate('/dashboards/keyword-intelligence');
  };

  // Helper function to get rank icon and color
  const getRankDisplay = (rank) => {
    if (rank <= 3) {
      return { icon: FaCrown, color: 'yellow.500', bg: 'yellow.50' };
    } else if (rank <= 10) {
      return { icon: FaTrophy, color: 'green.500', bg: 'green.50' };
    }
    return { icon: FaArrowUp, color: 'blue.500', bg: 'blue.50' };
  };

  // Helper function to get KD color and label
  const getKDInfo = (kd) => {
    if (kd <= 20) return { color: 'green.500', label: 'Easy', progressColor: 'green' };
    if (kd <= 40) return { color: 'yellow.500', label: 'Medium', progressColor: 'yellow' };
    if (kd <= 60) return { color: 'orange.500', label: 'Hard', progressColor: 'orange' };
    return { color: 'red.500', label: 'Very Hard', progressColor: 'red' };
  };

  if (isLoading) {
    return (
      <Box
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        p={6}
        h="100%"
        minH="400px"
      >
        <Flex justify="center" align="center" h="100%">
          <Spinner size="lg" color="purple.500" thickness="3px" />
        </Flex>
      </Box>
    );
  }

  // Show empty state if no keyword data
  if (keywordMetrics.businessesWithKeywords === 0) {
    return (
      <Box
        bg={cardBg}
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        overflow="hidden"
        h="100%"
        minH="400px"
        transition="all 0.3s"
        _hover={{
          shadow: 'md',
          borderColor: 'purple.200',
        }}
      >
        {/* Header */}
        <Box px={4} py={3} borderBottomWidth="1px" borderColor={borderColor} bg={headerBg}>
          <Flex justify="space-between" align="center">
            <HStack spacing={2}>
              <Flex w="32px" h="32px" bg={iconBg} borderRadius="md" justify="center" align="center">
                <Icon as={FaSearch} color="purple.500" boxSize={4} />
              </Flex>
              <Text fontWeight="bold" fontSize="md" color={textColor}>
                Keyword Intelligence
              </Text>
            </HStack>
          </Flex>
        </Box>

        {/* Empty State */}
        <Flex direction="column" align="center" justify="center" p={6} minH="300px">
          <Flex w="48px" h="48px" bg="gray.100" borderRadius="full" justify="center" align="center" mb={3}>
            <Icon as={FaSearch} boxSize={5} color="gray.400" />
          </Flex>
          <Text fontWeight="medium" color={subtitleColor} textAlign="center" fontSize="sm">
            No keyword data available yet
          </Text>
          <Text fontSize="xs" color={subtitleColor} textAlign="center" mt={1}>
            Keyword intelligence will appear once businesses are enriched with SEO data
          </Text>
        </Flex>
      </Box>
    );
  }

  // Compact Keyword Row Component
  const KeywordRow = ({ kw, index, isOpportunity = false }) => {
    const rankDisplay = getRankDisplay(kw.rank || 99);
    const kdInfo = getKDInfo(kw.keywordDifficulty || 0);
    
    return (
      <Box
        py={2}
        px={2}
        borderRadius="md"
        bg={isOpportunity ? 'green.50' : (index % 2 === 0 ? 'gray.50' : 'transparent')}
        _hover={{ bg: isOpportunity ? 'green.100' : 'purple.50' }}
        transition="background 0.15s"
      >
        <Grid templateColumns="40px 1fr 70px 50px" gap={2} alignItems="center">
          {/* Rank Badge */}
          <Flex justify="center">
            <Tooltip label={`Rank #${kw.rank || '-'}`}>
              <Flex
                w="32px"
                h="32px"
                bg={rankDisplay.bg}
                borderRadius="md"
                justify="center"
                align="center"
                position="relative"
              >
                <Icon as={rankDisplay.icon} color={rankDisplay.color} boxSize={3} />
                <Text
                  position="absolute"
                  bottom="-1px"
                  right="-1px"
                  fontSize="8px"
                  fontWeight="bold"
                  color={rankDisplay.color}
                  bg="white"
                  borderRadius="full"
                  px={1}
                  lineHeight="1"
                >
                  {kw.rank || '-'}
                </Text>
              </Flex>
            </Tooltip>
          </Flex>
          
          {/* Keyword & Business */}
          <VStack align="start" spacing={0} overflow="hidden">
            <Tooltip label={kw.keyword}>
              <Text fontSize="xs" fontWeight="semibold" color={textColor} noOfLines={1}>
                {kw.keyword}
              </Text>
            </Tooltip>
            <HStack spacing={1}>
              <Icon as={FaBuilding} boxSize={2} color={subtitleColor} />
              <Text fontSize="2xs" color={subtitleColor} noOfLines={1}>
                {kw.businessName}
              </Text>
            </HStack>
          </VStack>
          
          {/* Volume */}
          <VStack align="end" spacing={0}>
            <Text fontSize="xs" fontWeight="bold" color="blue.600">
              {formatNumber(kw.searchVolume || 0)}
            </Text>
            <Text fontSize="2xs" color={subtitleColor}>
              /mo
            </Text>
          </VStack>
          
          {/* KD with mini progress */}
          <VStack align="center" spacing={0}>
            <Text fontSize="xs" fontWeight="semibold" color={kdInfo.color}>
              {kw.keywordDifficulty || 0}
            </Text>
            <Progress
              value={kw.keywordDifficulty || 0}
              size="xs"
              colorScheme={kdInfo.progressColor}
              w="100%"
              borderRadius="full"
            />
          </VStack>
        </Grid>
      </Box>
    );
  };

  return (
    <Box
      bg={cardBg}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="lg"
      overflow="hidden"
      h="100%"
      minH="400px"
      display="flex"
      flexDirection="column"
      transition="all 0.3s"
      _hover={{
        shadow: 'md',
        borderColor: 'purple.200',
      }}
    >
      {/* Compact Header */}
      <Box px={4} py={3} borderBottomWidth="1px" borderColor={borderColor} bg={headerBg}>
        <Flex justify="space-between" align="center">
          <HStack spacing={2}>
            <Flex w="32px" h="32px" bg={iconBg} borderRadius="md" justify="center" align="center">
              <Icon as={FaSearch} color="purple.500" boxSize={4} />
            </Flex>
            <VStack align="start" spacing={0}>
              <Text fontWeight="bold" fontSize="md" color={textColor} textTransform="uppercase" fontStyle="italic">
                Keyword Intelligence
              </Text>
              <Text fontSize="2xs" color={subtitleColor}>
                Competitive keyword analysis
              </Text>
            </VStack>
          </HStack>
          <Button
            size="xs"
            colorScheme="purple"
            variant="ghost"
            rightIcon={<FaChevronRight size={10} />}
            onClick={handleViewDashboard}
            fontSize="xs"
          >
            View All
          </Button>
        </Flex>
      </Box>

      {/* Content */}
      <Box p={3} flex={1} display="flex" flexDirection="column" overflow="hidden">
        <VStack spacing={3} align="stretch" flex={1}>
          
          {/* Compact Stats Bar */}
          <Grid templateColumns="repeat(4, 1fr)" gap={2}>
            <Box bg="purple.50" px={2} py={1.5} borderRadius="md" borderLeft="3px solid" borderColor="purple.400">
              <HStack spacing={1} justify="space-between">
                <Icon as={FaKeyboard} color="purple.500" boxSize={3} />
                <VStack spacing={0} align="end">
                  <Text fontSize="sm" fontWeight="bold" color="purple.700" lineHeight="1">
                    {formatNumber(keywordMetrics.totalKeywords)}
                  </Text>
                  <Text fontSize="2xs" color="purple.600">Keywords</Text>
                </VStack>
              </HStack>
            </Box>
            <Box bg="blue.50" px={2} py={1.5} borderRadius="md" borderLeft="3px solid" borderColor="blue.400">
              <HStack spacing={1} justify="space-between">
                <Icon as={FaGlobe} color="blue.500" boxSize={3} />
                <VStack spacing={0} align="end">
                  <Text fontSize="sm" fontWeight="bold" color="blue.700" lineHeight="1">
                    {formatNumber(keywordMetrics.totalSearchVolume)}
                  </Text>
                  <Text fontSize="2xs" color="blue.600">Searches</Text>
                </VStack>
              </HStack>
            </Box>
            <Box bg="green.50" px={2} py={1.5} borderRadius="md" borderLeft="3px solid" borderColor="green.400">
              <HStack spacing={1} justify="space-between">
                <Icon as={FaBuilding} color="green.500" boxSize={3} />
                <VStack spacing={0} align="end">
                  <Text fontSize="sm" fontWeight="bold" color="green.700" lineHeight="1">
                    {keywordMetrics.businessesWithKeywords}
                  </Text>
                  <Text fontSize="2xs" color="green.600">Tracked</Text>
                </VStack>
              </HStack>
            </Box>
            <Box bg="orange.50" px={2} py={1.5} borderRadius="md" borderLeft="3px solid" borderColor="orange.400">
              <HStack spacing={1} justify="space-between">
                <Icon as={FaRocket} color="orange.500" boxSize={3} />
                <VStack spacing={0} align="end">
                  <Text fontSize="sm" fontWeight="bold" color="orange.700" lineHeight="1">
                    {keywordMetrics.opportunities.length}
                  </Text>
                  <Text fontSize="2xs" color="orange.600">Opps</Text>
                </VStack>
              </HStack>
            </Box>
          </Grid>

          {/* Two Column Layout for Rankings and Opportunities */}
          <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={3} flex={1} overflow="hidden">
            
            {/* Top Rankings Column */}
            <GridItem overflow="hidden" display="flex" flexDirection="column">
              <HStack spacing={1} mb={2}>
                <Icon as={FaTrophy} color="yellow.500" boxSize={3} />
                <Text fontSize="xs" fontWeight="bold" color={textColor}>
                  Top Rankings
                </Text>
                <Badge colorScheme="yellow" fontSize="2xs" variant="subtle">
                  {keywordMetrics.keywordRankings.length}
                </Badge>
              </HStack>
              
              <VStack spacing={0} align="stretch" flex={1} overflow="auto">
                {keywordMetrics.keywordRankings.length > 0 ? (
                  keywordMetrics.keywordRankings.slice(0, 4).map((kw, index) => (
                    <KeywordRow key={`ranking-${kw.keyword}-${index}`} kw={kw} index={index} />
                  ))
                ) : (
                  <Flex p={3} bg="gray.50" borderRadius="md" justify="center" align="center" flex={1}>
                    <Text fontSize="xs" color={subtitleColor}>No ranking data</Text>
                  </Flex>
                )}
              </VStack>
            </GridItem>

            {/* Opportunities Column */}
            <GridItem overflow="hidden" display="flex" flexDirection="column">
              <HStack spacing={1} mb={2}>
                <Icon as={FaLightbulb} color="green.500" boxSize={3} />
                <Text fontSize="xs" fontWeight="bold" color={textColor}>
                  Opportunities
                </Text>
                <Badge colorScheme="green" fontSize="2xs" variant="subtle">
                  {keywordMetrics.opportunities.length}
                </Badge>
              </HStack>
              
              <VStack spacing={0} align="stretch" flex={1} overflow="auto">
                {keywordMetrics.opportunities.length > 0 ? (
                  keywordMetrics.opportunities.slice(0, 4).map((kw, index) => (
                    <KeywordRow key={`opp-${kw.keyword}-${index}`} kw={kw} index={index} isOpportunity />
                  ))
                ) : (
                  <Flex p={3} bg="green.50" borderRadius="md" justify="center" align="center" flex={1}>
                    <Text fontSize="xs" color={subtitleColor}>No opportunities found</Text>
                  </Flex>
                )}
              </VStack>
            </GridItem>
          </Grid>

          {/* High Volume Keywords - Compact Tags */}
          {keywordMetrics.topKeywords.length > 0 && (
            <Box pt={1}>
              <HStack spacing={1} mb={1.5}>
                <Icon as={FaFire} color="red.400" boxSize={3} />
                <Text fontSize="xs" fontWeight="bold" color={textColor}>
                  High Volume
                </Text>
              </HStack>
              <Flex gap={1} flexWrap="wrap">
                {keywordMetrics.topKeywords.slice(0, 8).map((kw, index) => (
                  <Tooltip 
                    key={`vol-${kw.keyword}-${index}`} 
                    label={`${formatNumber(kw.searchVolume || 0)}/mo • KD: ${kw.keywordDifficulty || 0}${kw.rank ? ` • #${kw.rank}` : ''} • ${kw.businessName}`}
                    placement="top"
                    hasArrow
                  >
                    <Badge
                      px={1.5}
                      py={0.5}
                      borderRadius="md"
                      bg={index === 0 ? 'purple.100' : index === 1 ? 'blue.50' : 'gray.100'}
                      color={index === 0 ? 'purple.700' : index === 1 ? 'blue.700' : 'gray.600'}
                      fontSize="2xs"
                      fontWeight="medium"
                      cursor="pointer"
                      _hover={{ transform: 'scale(1.05)', shadow: 'sm' }}
                      transition="all 0.15s"
                    >
                      {kw.keyword.length > 15 ? kw.keyword.substring(0, 15) + '…' : kw.keyword}
                    </Badge>
                  </Tooltip>
                ))}
              </Flex>
            </Box>
          )}
        </VStack>
      </Box>
    </Box>
  );
};

export default KeywordIntelligenceWidget;
